import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './chisono.scss';

gsap.registerPlugin(ScrollTrigger);

const skills = [
  { name: 'Next.js / React', level: 95 },
  { name: 'UI/UX Design', level: 90 },
  { name: 'HTML / CSS / SCSS', level: 95 },
  { name: 'Figma / Adobe XD', level: 85 },
  { name: 'Node.js / Express', level: 70 },
  { name: 'Git / GitHub', level: 85 },
];

const experiences = [
  {
    year: '2022 - Oggi',
    role: 'Freelance Web Designer & Developer',
    company: 'CAMILLACINODESIGN&DEV',
    description: 'Progettazione e sviluppo di siti web e web app per PMI e professionisti.'
  },
  {
    year: '2023 - 2024',
    role: 'Web Developer',
    company: 'Agenzie di comunicazione',
    description: 'Collaborazione con diverse agenzie per la realizzazione di progetti digitali.'
  },
  {
    year: '2021 - 2022',
    role: 'Web Developer Junior',
    company: 'Agenzie di comunicazione',
    description: 'Collaborazione con agenzie per la realizzazione di siti web custom.'
  }
];

function Chisono() {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Hero title animation
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Video parallax effect
    const handleScroll = () => {
      if (videoRef.current) {
        const scrolled = window.pageYOffset;
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Skills animation
    if (skillsRef.current) {
      const bars = skillsRef.current.querySelectorAll('.skill-progress');
      bars.forEach((bar) => {
        gsap.fromTo(bar,
          { width: 0 },
          {
            width: bar.getAttribute('data-level') + '%',
            duration: 1.5,
            ease: "power2.out",
            scrollTrigger: {
              trigger: bar,
              start: "top 90%",
              toggleActions: "play none none none"
            }
          }
        );
      });
    }

    // Cards animation
    const cards = document.querySelectorAll('.experience-card, .download-card');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Camilla Cino | Web Designer Freelance Modena - Chi Sono</title>
        <meta name="description" content="Sono Camilla, web designer e developer freelance a Modena. 5+ anni di esperienza, 50+ progetti in React e TypeScript. Collaboro con PMI e professionisti in tutta Italia." />
        <meta name="keywords" content="UI UX designer, web developer freelance, sviluppatrice web, designer freelance, React developer, portfolio web designer, Modena, web designer Modena" />
        <meta property="og:title" content="Camilla Cino | Web Designer Freelance Modena - Chi Sono" />
        <meta property="og:description" content="Sono Camilla, web designer e developer freelance a Modena. 5+ anni di esperienza, 50+ progetti in React e TypeScript." />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/chisono" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/chisono" />
      </Helmet>

      <div className="chisono-page">
        {/* Hero Section */}
        <section className="chisono-hero">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video3.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <div className="hero-label">
                  <span className="label-line"></span>
                  Chi Sono
                </div>

                <h1 className="hero-title" ref={heroTitleRef}>
                  <span className="hero-line">Ciao, sono</span>
                  <span className="hero-line"><span className="gradient-text">Camilla</span></span>
                </h1>

                <p className="hero-description">
                  <strong>Sviluppatrice Web</strong> e <strong>UI/UX Designer</strong> con anni di esperienza
                  nella creazione di siti web e applicazioni. Collaboro con PMI e professionisti
                  per trasformare le loro idee in prodotti digitali funzionali e memorabili.
                </p>

                <div className="hero-cta-group">
                  <a
                    href="#download"
                    className="hero-cta"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Scarica CV
                    <span className="arrow">↓</span>
                  </a>
                  <button
                    className="hero-cta secondary"
                    onClick={() => navigateWithTransition('/contatti')}
                  >
                    Contattami
                    <span className="arrow">→</span>
                  </button>
                </div>
              </div>

              <div className="hero-image">
                <div className="image-wrapper">
                  <img src="/chi_sono/me.png" alt="Camilla Cino - Web designer freelance" />
                </div>
              </div>
            </div>
          </Container>

          <div className="scroll-indicator">
            <div className="scroll-line"></div>
            <span>Scroll</span>
          </div>
        </section>

        {/* Skills Section */}
        <section className="skills-section">
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">01</span>
                Competenze
              </h2>
            </div>

            <div className="skills-grid" ref={skillsRef}>
              {skills.map((skill, index) => (
                <div key={index} className="skill-item">
                  <div className="skill-header">
                    <span className="skill-name">{skill.name}</span>
                    <span className="skill-percent">{skill.level}%</span>
                  </div>
                  <div className="skill-bar">
                    <div
                      className="skill-progress"
                      data-level={skill.level}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Experience Section */}
        <section className="experience-section">
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">02</span>
                Esperienza
              </h2>
            </div>

            <div className="experience-grid">
              {experiences.map((exp, index) => (
                <div key={index} className="experience-card">
                  <span className="exp-year">{exp.year}</span>
                  <h3 className="exp-role">{exp.role}</h3>
                  <span className="exp-company">{exp.company}</span>
                  <p className="exp-description">{exp.description}</p>
                  {exp.link && (
                    <a href={exp.link} target="_blank" rel="noopener noreferrer" className="exp-cta">
                      Scopri di più <span className="arrow">→</span>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Download Section */}
        <section className="download-section" id="download" style={{ backgroundImage: "url('/sfondi/sfondo3.png')" }}>
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">03</span>
                Scarica
              </h2>
            </div>

            <div className="download-grid">
              <a href="/pdf/CV.pdf" download="CV_Camilla_Cino.pdf" className="download-card">
                <div className="card-icon">{'<>'}</div>
                <h3 className="card-title">Curriculum Vitae</h3>
                <p className="card-desc">Il mio percorso professionale</p>
                <span className="download-btn">Download PDF →</span>
              </a>

              <a href="/pdf/portfolio_camilla_cino.pdf" download="Portfolio_Camilla_Cino.pdf" className="download-card">
                <div className="card-icon figma">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M7 7h10v10H7z" fill="#FF7262"/>
                    <path d="M7 17h10v3.5a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5V17z" fill="#1ABCFE"/>
                    <path d="M17 7h3.5a.5.5 0 01.5.5v9a.5.5 0 01-.5.5H17V7z" fill="#0ACF83"/>
                    <path d="M7 7H3.5a.5.5 0 00-.5.5v9a.5.5 0 00.5.5H7V7z" fill="#A259FF"/>
                  </svg>
                </div>
                <h3 className="card-title">Portfolio Grafico</h3>
                <p className="card-desc">I miei progetti di UI/UX design e branding</p>
                <span className="download-btn">Download PDF →</span>
              </a>
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>
      </div>
    </>
  );
}

export default Chisono;
